import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Settings, Globe, Server, Compass, CheckCircle, AlertCircle } from 'lucide-react';
import { LLM_CONFIG, DEFAULT_MODEL_CONFIGS, WEBLLM_STATE, DEFAULT_MODEL_CONFIG, GOOGLE_MODEL_LIST, DEFAULT_CODE_ASSIST_MODEL, buildCodeAssistModelList } from '../constants/graphConstants';
import WebLLMProgressTracker from '../components/WebLLMProgressTracker';
import { Z } from '../constants/zLayers';
import { isGoogleSignInConfigured, isSignedIn, signIn } from '../utils/googleAuth';
import CodeAssistSignIn from './CodeAssistSignIn';
import { isSignedIn as caSignedIn, hasStoredGrant, AUTH_PROVIDERS, DEFAULT_PROVIDER, canReachAntigravity } from '../utils/codeAssistAuth';
import { loadCodeAssist, discoverModels } from '../utils/codeAssist';

const ModelSelector = ({
    currentModel,
    onModelChange,
    connectionStatus,
    onTestConnection,
    webllmLoadingProgress,
    webllmLoadState,
    onOpenChange,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const signInAvailable = isGoogleSignInConfigured();
    const [authMethod, setAuthMethod] = useState('code-assist');
    const providers = Object.entries(AUTH_PROVIDERS);

    // Which desktop client's grant to sign in with. They are separate grants
    // reaching the same API, and Antigravity's is served newer models.
    //
    // Both of these seed from the running config, like every other tab does.
    // Defaulting them instead made reopening the panel silently rewrite the
    // choice: it came back on Gemini CLI showing that client's default, and
    // Apply then sent that rather than what was running.
    const isCodeAssist = currentModel?.type === 'code-assist';
    const [authProvider, setAuthProvider] = useState(
        () => (isCodeAssist ? currentModel.authProvider ?? DEFAULT_PROVIDER : DEFAULT_PROVIDER)
    );
    const [caReady, setCaReady] = useState(() => caSignedIn() || hasStoredGrant());

    // Read after authProvider exists: computing it above the useState threw on
    // every render - a temporal dead zone - and a component that throws while
    // rendering takes the whole page with it, start screen included.
    //
    // Antigravity's models are served on the strength of a User-Agent no web
    // page may set, so they cannot be fetched from an ordinary browser. It is
    // still offered rather than hidden: hiding it left no trace of a whole
    // backend, and it is what someone opening this inside Antigravity's own
    // browser is looking for. The panel says so instead.
    const antigravityBlocked = AUTH_PROVIDERS[authProvider]?.requiresAntigravityAgent
        && !canReachAntigravity();
    // Kept apart from externalConfig.model: the two backends do not accept the
    // same ids, so carrying one across would send a name the other rejects.
    const [caModel, setCaModel] = useState(
        () => (isCodeAssist ? currentModel.model : DEFAULT_CODE_ASSIST_MODEL)
    );

    const [signInBusy, setSignInBusy] = useState(false);
    const [signInError, setSignInError] = useState(null);
    const [signedIn, setSignedIn] = useState(() => isSignedIn());
    // google-oauth lives under the External tab; it is a way of authenticating to
    // Google, not a separate place to configure.
    const tabForType = (type) => (type === 'google-oauth' || type === 'code-assist' ? 'external' : type);
    const [activeTab, setActiveTab] = useState(tabForType(currentModel.type) || 'webllm');
    const dropdownRef = useRef(null);

    const shouldShowProgressTracker = (webllmState) => {
        return ((webllmState == WEBLLM_STATE.RELOADING) || (webllmState == WEBLLM_STATE.DOWNLOADING));
    };

    // Seed each tab from the model actually in use, so the panel shows what is
    // running rather than the defaults. Previously the selection always read
    // back as the default, which made a saved-but-different model invisible.
    const fromCurrent = useCallback((type, fallback) => (
        currentModel?.type === type ? { ...fallback, ...currentModel } : fallback
    ), [currentModel]);

    const [localConfig, setLocalConfig] = useState(() => fromCurrent('local', {
        address: DEFAULT_MODEL_CONFIGS.LOCAL.address,
        model: DEFAULT_MODEL_CONFIGS.LOCAL.model
    }));

    const [externalConfig, setExternalConfig] = useState(() => fromCurrent('external', {
        provider: DEFAULT_MODEL_CONFIGS.EXTERNAL.provider,
        model: DEFAULT_MODEL_CONFIGS.EXTERNAL.model,
        apiKey: currentModel.apiKey || ''
    }));

    const [webllmConfig, setWebllmConfig] = useState(() => fromCurrent('webllm', {
        model: DEFAULT_MODEL_CONFIGS.WEBLLM.model
    }));

    // The saved config is restored asynchronously at startup, after this panel
    // has already mounted, so seeding at first render is not enough on its own.
    useEffect(() => {
        if (currentModel?.type === 'local') setLocalConfig(prev => ({ ...prev, ...currentModel }));
        if (['external', 'google-oauth', 'code-assist'].includes(currentModel?.type)) setExternalConfig(prev => ({ ...prev, ...currentModel }));
        if (currentModel?.type === 'webllm') setWebllmConfig(prev => ({ ...prev, ...currentModel }));
        // Only while closed - switching the tab under an open panel would move
        // the controls out from under the user.
        if (!isOpen && currentModel?.type) setActiveTab(tabForType(currentModel.type));
    }, [currentModel, isOpen]);

    // The saved config is restored after this panel has mounted, so first-render
    // seeding is not enough - the same reason localConfig/externalConfig have
    // their own sync effect. Only while closed: re-seeding under an open panel
    // would move the controls out from under the user mid-edit.
    useEffect(() => {
        if (isOpen || currentModel?.type !== 'code-assist') return;
        setAuthProvider(currentModel.authProvider ?? DEFAULT_PROVIDER);
        if (currentModel.model) setCaModel(currentModel.model);
    }, [currentModel, isOpen]);

    // Load saved API key on component mount
    useEffect(() => {
        const savedApiKey = localStorage.getItem('graphible-google-api-key');
        if (savedApiKey && !externalConfig.apiKey) {
            setExternalConfig(prev => ({ ...prev, apiKey: savedApiKey }));
        }
    }, [externalConfig.apiKey]);

    // The menu cannot escape its own stacking context, so whoever renders this
    // has to raise the container while it is open.
    useEffect(() => {
        onOpenChange?.(isOpen);
    }, [isOpen, onOpenChange]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // What this account actually has, asked of the backend rather than assumed.
    // The static catalog can only name models that need no per-account access,
    // so a preview model the user is entitled to would otherwise be invisible -
    // and one they are not entitled to would be offered and fail. Empty until
    // it answers, which keeps the static list showing.
    const [discoveredModels, setDiscoveredModels] = useState([]);
    // Distinguishes "not asked yet" from "asked and got nothing", which decide
    // different things: the first shows a wait, the second has to fall back to
    // something usable or leave the user with an empty menu.
    const [discoveryTried, setDiscoveryTried] = useState(false);

    // Signed in to one client says nothing about the other, and neither does
    // what the last one had access to.
    useEffect(() => {
        setCaReady(caSignedIn(authProvider) || hasStoredGrant(authProvider));
        setDiscoveredModels([]);
        setDiscoveryTried(false);
    }, [authProvider]);

    useEffect(() => {
        if (!caReady || discoveredModels.length) return;
        let cancelled = false;

        // Two network round trips stand between opening this panel and seeing
        // any model. They are usually quick and sometimes not, and a panel that
        // stays empty while they hang is indistinguishable from a broken one -
        // so the fallback is shown on a timer, and a late answer still replaces
        // it.
        const settle = setTimeout(() => { if (!cancelled) setDiscoveryTried(true); }, 4000);

        (async () => {
            try {
                const { project } = await loadCodeAssist(authProvider);
                const ids = await discoverModels(project, authProvider);
                if (!cancelled) setDiscoveredModels(ids);
            } catch {
                // Nothing to surface: the fallback below keeps the panel usable.
            } finally {
                if (!cancelled) setDiscoveryTried(true);
            }
        })();

        return () => { cancelled = true; clearTimeout(settle); };
    }, [caReady, discoveredModels.length, authProvider]);

    const codeAssistModels = buildCodeAssistModelList(discoveredModels);

    // Until the account has answered there is no honest list to show: which
    // models exist depends on the project the server resolves, and that is not
    // knowable from here. Showing a plausible one instead is what put ids in
    // the menu that the resolved project had never heard of.
    // ...but an account that will not answer must still leave a usable menu, so
    // after a failed attempt the shared seed stands in - the four models both
    // projects serve, which cannot 404 whichever one was resolved.
    const modelsAreKnown = authMethod !== 'code-assist'
        || discoveredModels.length > 0
        || (caReady && discoveryTried);

    // Discovery can rule out the model already selected. Left alone it would sit
    // in the panel unlisted and still be what Apply sends.
    //
    // Only once discovery has actually answered: the seed catalogs are a guess,
    // and letting a guess overrule a running model is how a working choice gets
    // replaced by a default.
    useEffect(() => {
        if (!discoveredModels.length) return;
        if (!codeAssistModels.some((m) => m.id === caModel)) {
            setCaModel((codeAssistModels.find((m) => m.recommended) ?? codeAssistModels[0])?.id);
        }
    }, [discoveredModels.length, codeAssistModels, caModel]);

    // Which model list the External tab is currently editing.
    const selectedModel = authMethod === 'code-assist' ? caModel : externalConfig.model;
    const selectModel = (id) => (authMethod === 'code-assist'
        ? setCaModel(id)
        : setExternalConfig(prev => ({ ...prev, model: id })));

    const handleGoogleSignIn = async () => {
        setSignInBusy(true);
        setSignInError(null);
        try {
            await signIn();
            setSignedIn(true);
        } catch (error) {
            setSignInError(error.message);
            setSignedIn(false);
        } finally {
            setSignInBusy(false);
        }
    };

    const handleSave = () => {
        let config;

        if (activeTab === 'local') {
            config = { type: 'local', ...localConfig };
        } else if (activeTab === 'external') {
            // Signed-in Google and a pasted key are different backends, not
            // two settings of one: they authenticate and bill differently.
            if (authMethod === 'code-assist') {
                config = { type: 'code-assist', provider: 'google', authProvider, model: caModel };
            } else if (authMethod === 'oauth') {
                config = { type: 'google-oauth', provider: 'google', model: externalConfig.model, projectId: externalConfig.projectId?.trim() || '' };
            } else {
                config = { type: 'external', ...externalConfig };
            }
        } else if (activeTab === 'webllm') {
            config = { type: 'webllm', ...webllmConfig };
        }

        // Save Google API key to localStorage if it's an external config
        if (activeTab === 'external' && externalConfig.apiKey.trim()) {
            localStorage.setItem('graphible-google-api-key', externalConfig.apiKey.trim());
        }

        onModelChange(config);
        console.log(config);
        setIsOpen(false);
        // Test the connection with new config
        // Applying a model is deliberate, so this test may prompt to download.
        setTimeout(() => onTestConnection(config, { interactive: true }), 100);
    };

    const getStatusColor = () => {
        switch (connectionStatus) {
            case 'connected': return 'text-emerald-600 dark:text-emerald-400';
            case 'pending': return 'text-amber-600 dark:text-amber-400';
            default: return 'text-rose-600 dark:text-rose-400';
        }
    };

    const getDisplayName = () => {
        if (['external', 'google-oauth', 'code-assist'].includes(currentModel.type)) {
            const model = GOOGLE_MODEL_LIST.find(m => m.id === currentModel.model);
            return model ? model.name : currentModel.model;
        } else if (currentModel.type === 'webllm') {
            const model = LLM_CONFIG.WEBLLM[currentModel.model];
            return model ? model.name : currentModel.model;
        }
        return `${currentModel.model || 'No model detected'}`;
    };

    const getDisplayIcon = useCallback(() => {
        if (['external', 'google-oauth', 'code-assist'].includes(currentModel.type)) return Globe;
        if (currentModel.type === 'webllm') return Compass;
        return Server;
    }, [currentModel.type]);

    const DisplayIcon = getDisplayIcon();

    return (
        <div className="relative font-inter" ref={dropdownRef} style={{ zIndex: isOpen ? Z.DROPDOWN : undefined }}>
            {/* Main Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-3 px-4 py-2 bg-white/80 dark:bg-neutral-900/80 border border-slate-200 dark:border-neutral-800 rounded-lg text-slate-700 dark:text-neutral-200 hover:border-slate-300 dark:hover:border-neutral-700 hover:bg-white dark:hover:bg-neutral-800 transition-all duration-300 shadow-sm"
                style={{
                    boxShadow: isOpen ? '0 0 0 3px rgb(148 163 184 / 0.1)' : undefined,
                }}
            >
                <div className="flex items-center gap-2">
                    <DisplayIcon size={16} className={
                        ['external', 'google-oauth', 'code-assist'].includes(currentModel.type) ? 'text-indigo-600 dark:text-indigo-400' :
                            currentModel.type === 'webllm' ? 'text-purple-600 dark:text-purple-400' :
                                'text-slate-600 dark:text-neutral-300'
                    } />
                    <span className="text-sm font-medium">{getDisplayName()}</span>
                    <div className={`w-2 h-2 rounded-full ${getStatusColor().replace('text-', 'bg-').replace('400', '500')}`} />
                </div>
                <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Progress indicator for WebLLM */}
            {shouldShowProgressTracker(webllmLoadState) && webllmLoadingProgress && (<WebLLMProgressTracker webllmLoadState={webllmLoadState} progress={webllmLoadingProgress} modelName={currentModel.model}/>)}

            {/* Dropdown */}
            {isOpen && (
                <div
                    // Bounded to the viewport: the panel is taller than a
                    // laptop screen, and the page itself does not scroll, so an
                    // unbounded panel put "Apply Settings" permanently out of
                    // reach and the chosen model could never be applied.
                    className="absolute top-full left-0 mt-2 w-96 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl shadow-xl flex flex-col max-h-[calc(100vh-6rem)]"
                    style={{ zIndex: Z.DROPDOWN }}
                >
                    {/* Tab Headers */}
                    <div className="flex border-b border-slate-200 dark:border-neutral-800 flex-shrink-0 rounded-t-xl overflow-hidden">
                        <button
                            onClick={() => setActiveTab('webllm')}
                            className={`flex-1 px-3 py-3 text-sm font-medium transition-all duration-200 relative ${activeTab === 'webllm'
                                ? 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-500/10'
                                : 'text-slate-600 dark:text-neutral-300 hover:text-slate-800 dark:hover:text-neutral-100 hover:bg-slate-50 dark:hover:bg-neutral-800'
                                }`}
                        >
                            <div className="flex items-center gap-2 justify-center">
                                <Compass size={14} />
                                Browser
                            </div>
                            {activeTab === 'webllm' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-700" />
                            )}
                        </button>

                        <button
                            onClick={() => setActiveTab('local')}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-all duration-200 relative ${activeTab === 'local'
                                ? 'text-slate-800 dark:text-neutral-100 bg-slate-50 dark:bg-neutral-800/50'
                                : 'text-slate-600 dark:text-neutral-300 hover:text-slate-800 dark:hover:text-neutral-100 hover:bg-slate-50 dark:hover:bg-neutral-800'
                                }`}
                        >
                            <div className="flex items-center gap-2 justify-center z-2">
                                <Server size={16} />
                                Local Model
                            </div>
                            {activeTab === 'local' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-800" />
                            )}
                        </button>

                        <button
                            onClick={() => setActiveTab('external')}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-all duration-200 relative ${activeTab === 'external'
                                ? 'text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10'
                                : 'text-slate-600 dark:text-neutral-300 hover:text-slate-800 dark:hover:text-neutral-100 hover:bg-slate-50 dark:hover:bg-neutral-800'
                                }`}
                        >
                            <div className="flex items-center gap-2 justify-center">
                                <Globe size={16} />
                                External API
                            </div>
                            {activeTab === 'external' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-700" />
                            )}
                        </button>
                    </div>

                    {/* Tab Content */}
                    <div className="p-4 overflow-y-auto flex-1 min-h-0">
                        {activeTab === 'webllm' && (
                            <div className="space-y-4">
                                <div className="bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/30 rounded-lg p-3 mb-4">
                                    <div className="flex items-start gap-2">
                                        <Compass className="text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" size={16} />
                                        <div>
                                            <h3 className="font-semibold text-purple-800 dark:text-purple-200 mb-1 text-sm">AI in Your Browser</h3>
                                            <div className="text-purple-700 dark:text-purple-300 text-xs space-y-1">
                                                <div className="flex items-center gap-1">
                                                    <CheckCircle size={10} />
                                                    <span>No installation required</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <CheckCircle size={10} />
                                                    <span>Complete privacy - data stays local</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <CheckCircle size={10} />
                                                    <span>Works offline after initial download</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-neutral-200 mb-2">
                                        Select Model
                                    </label>
                                    <div className="space-y-2">
                                        {Object.entries(LLM_CONFIG.WEBLLM).map(([modelId, modelInfo]) => (
                                            <label
                                                key={modelId}
                                                className={`flex items-center p-3 border rounded cursor-pointer transition-all duration-200 ${webllmConfig.model === modelId
                                                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-500/10'
                                                    : 'border-slate-200 dark:border-neutral-800 hover:border-purple-300 dark:hover:border-purple-400/50 hover:bg-purple-25 dark:hover:bg-purple-500/5'
                                                    }`}
                                                onClick={() => setWebllmConfig(prev => ({ ...prev, model: modelId }))}
                                            >
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <div className="font-medium text-sm text-slate-800 dark:text-neutral-100">{modelInfo.name}</div>
                                                        {modelInfo.recommended && (
                                                            <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                                                                Recommended
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-neutral-400">
                                                        <span>Size: {modelInfo.size}</span>
                                                        <span>Performance: {modelInfo.performance}</span>
                                                    </div>
                                                </div>
                                                <div className={`w-4 h-4 border-2 rounded-full transition-all duration-200 ${webllmConfig.model === modelId
                                                    ? 'border-purple-500 bg-purple-500'
                                                    : 'border-slate-300 dark:border-neutral-700'
                                                    }`}>
                                                    {webllmConfig.model === modelId && (
                                                        <div className="w-2 h-2 bg-white dark:bg-neutral-900 rounded-full m-0.5" />
                                                    )}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg p-3">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" size={14} />
                                        <div className="text-amber-700 dark:text-amber-300 text-xs">
                                            <div className="font-semibold mb-1">Requirements:</div>
                                            <div>• Chrome/Edge 113+, Firefox 141+, or Safari 26+</div>
                                            <div>• First download may take 1-3 minutes</div>
                                            <div>• 8GB+ RAM recommended for larger models</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'local' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-black-300 mb-2">
                                        Server Address
                                    </label>
                                    <input
                                        type="text"
                                        value={localConfig.address}
                                        onChange={(e) => setLocalConfig(prev => ({ ...prev, address: e.target.value }))}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        placeholder="http://localhost:11434"
                                        className="w-full px-3 py-2 bg-white-800 dark:bg-neutral-900 border border-gray-600 dark:border-neutral-700 rounded text-black dark:text-neutral-100 placeholder-gray-400 dark:placeholder-neutral-500 focus:border-blue-500 focus:outline-none transition-all duration-200 group"
                                        onFocus={(e) => {
                                            e.target.style.boxShadow = '0 0 15px rgba(59, 130, 246, 0.2)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.boxShadow = 'none';
                                        }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-black-300 mb-2">
                                        Model Name
                                    </label>
                                    <input
                                        type="text"
                                        value={localConfig.model}
                                        onChange={(e) => setLocalConfig(prev => ({ ...prev, model: e.target.value }))}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        placeholder={DEFAULT_MODEL_CONFIG.model}
                                        className="w-full px-3 py-2 bg-white-800 dark:bg-neutral-900 border border-gray-600 dark:border-neutral-700 rounded text-black dark:text-neutral-100 placeholder-gray-400 dark:placeholder-neutral-500 focus:border-blue-500 focus:outline-none transition-all duration-200"
                                        onFocus={(e) => {
                                            e.target.style.boxShadow = '0 0 15px rgba(59, 130, 246, 0.2)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.boxShadow = 'none';
                                        }}
                                    />
                                </div>

                                <div className="text-xs text-black-400 bg-white-800/50 p-3 rounded">
                                    <div className="font-semibold mb-1">Local Setup Instructions:</div>
                                    <div>1. Install <span className="text-blue-400">Ollama</span></div>
                                    <div>2. Run: <code className="bg-gray-100 dark:bg-neutral-800 dark:text-neutral-200 px-1 rounded">OLLAMA_ORIGINS=* ollama serve</code></div>
                                    <div>3. Pull model: <code className="bg-gray-100 dark:bg-neutral-800 dark:text-neutral-200 px-1 rounded">ollama pull {LLM_CONFIG.LOCAL.DEFAULT_MODEL}</code></div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'external' && (
                            <div className="space-y-4">
                                {/* Provider select, hidden while Google is the only option.
                                    Kept rather than deleted so routing a second provider
                                    through here does not mean rebuilding the control.
                                    externalConfig.provider still defaults to 'google'.

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 dark:text-neutral-300 mb-2">
                                        Provider
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={externalConfig.provider}
                                            onChange={(e) => setExternalConfig(prev => ({
                                                ...prev,
                                                provider: e.target.value,
                                                model: DEFAULT_MODEL_CONFIGS.EXTERNAL.model // Reset to default model
                                            }))}
                                            className="w-full px-3 py-2 bg-gray-800 dark:bg-neutral-800 border border-gray-600 dark:border-neutral-700 rounded text-white focus:border-purple-500 focus:outline-none transition-all duration-200 appearance-none"
                                            onFocus={(e) => {
                                                e.target.style.boxShadow = '0 0 15px rgba(147, 51, 234, 0.2)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        >
                                            <option value="google">Google AI</option>
                                        </select>
                                        <ChevronDown size={16} className="absolute right-3 top-3 text-gray-400 dark:text-neutral-400 pointer-events-none" />
                                    </div>
                                </div>
                                */}

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-neutral-200 mb-2">
                                        Model
                                    </label>
                                    {!modelsAreKnown && (
                                        <p className="text-xs text-slate-500 dark:text-neutral-400 mb-2">
                                            {caReady
                                                ? 'Reading the models on your account\u2026'
                                                : 'Sign in below and your account\u2019s own models are listed here.'}
                                        </p>
                                    )}
                                    <div className="space-y-2">
                                        {(authMethod === 'code-assist'
                                            ? (modelsAreKnown ? codeAssistModels : [])
                                            : GOOGLE_MODEL_LIST).map((model) => (
                                            <label
                                                key={model.id}
                                                className={`flex items-center p-3 border rounded cursor-pointer transition-all duration-200 group ${selectedModel === model.id
                                                    ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                                                    : 'border-gray-600 dark:border-neutral-700 hover:border-purple-400 hover:bg-purple-500/5'
                                                    }`}
                                                onMouseEnter={(e) => {
                                                    if (externalConfig.model !== model.id) {
                                                        e.currentTarget.style.boxShadow = '0 0 15px rgba(147, 51, 234, 0.1)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (externalConfig.model !== model.id) {
                                                        e.currentTarget.style.boxShadow = 'none';
                                                    }
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="model"
                                                    value={model.id}
                                                    checked={selectedModel === model.id}
                                                    onChange={(e) => selectModel(e.target.value)}
                                                    className="sr-only"
                                                />
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm">{model.name}</div>
                                                    <div className="text-xs text-slate-600 dark:text-neutral-300">{model.description}</div>
                                                    {/* The wire id, because the labels are our transliteration of it
                                                        and some of these are internal names Google does not publish. */}
                                                    <div className="text-[10px] text-slate-400 dark:text-neutral-500 font-mono mt-0.5">{model.id}</div>
                                                </div>
                                                <div className={`w-4 h-4 border-2 rounded-full transition-all duration-200 ${selectedModel === model.id
                                                    ? 'border-purple-500 bg-purple-500'
                                                    : 'border-gray-400 dark:border-neutral-400'
                                                    }`}>
                                                    {selectedModel === model.id && (
                                                        <div className="w-2 h-2 bg-white dark:bg-neutral-900 rounded-full m-0.5" />
                                                    )}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex rounded-lg border border-slate-300 dark:border-neutral-700 overflow-hidden text-sm">
                                        <button
                                            type="button"
                                            onClick={() => setAuthMethod('code-assist')}
                                            className={`flex-1 px-3 py-2 transition-colors ${authMethod === 'code-assist'
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-white dark:bg-neutral-900 text-slate-600 dark:text-neutral-300 hover:bg-slate-50 dark:hover:bg-neutral-800'}`}
                                        >
                                            Google account
                                        </button>
                                        {signInAvailable && (
                                            <button
                                                type="button"
                                                onClick={() => setAuthMethod('oauth')}
                                                className={`flex-1 px-3 py-2 transition-colors ${authMethod === 'oauth'
                                                    ? 'bg-indigo-600 text-white'
                                                    : 'bg-white dark:bg-neutral-900 text-slate-600 dark:text-neutral-300 hover:bg-slate-50 dark:hover:bg-neutral-800'}`}
                                            >
                                                Own project
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => setAuthMethod('apikey')}
                                            className={`flex-1 px-3 py-2 transition-colors ${authMethod === 'apikey'
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-white dark:bg-neutral-900 text-slate-600 dark:text-neutral-300 hover:bg-slate-50 dark:hover:bg-neutral-800'}`}
                                        >
                                            Use an API key
                                        </button>
                                    </div>

                                    {authMethod === 'code-assist' && (
                                        <div className="space-y-3">
                                            {providers.length > 1 && (
                                                <div className="flex rounded-lg border border-slate-200 dark:border-neutral-800 overflow-hidden text-xs">
                                                    {providers.map(([key, { label }]) => (
                                                        <button
                                                            key={key}
                                                            type="button"
                                                            onClick={() => setAuthProvider(key)}
                                                            className={`flex-1 px-3 py-1.5 transition-colors ${authProvider === key
                                                                ? 'bg-slate-800 text-white'
                                                                : 'bg-white dark:bg-neutral-900 text-slate-600 dark:text-neutral-300 hover:bg-slate-50 dark:hover:bg-neutral-800'}`}
                                                        >
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {antigravityBlocked && (
                                                <p className="text-xs text-slate-500 dark:text-neutral-400">
                                                    Works here. Antigravity&apos;s larger catalogue is served only to its
                                                    own browser, which identifies itself in a header no web page may set
                                                    &mdash; signing in lists whichever models your account actually has.
                                                </p>
                                            )}

                                            <CodeAssistSignIn
                                                signedIn={caReady}
                                                onSignedInChange={setCaReady}
                                                provider={authProvider}
                                            />
                                        </div>
                                    )}

                                    {authMethod === 'oauth' && (
                                        <div className="space-y-2">
                                            <button
                                                type="button"
                                                onClick={handleGoogleSignIn}
                                                disabled={signInBusy}
                                                className="w-full px-4 py-2.5 border border-slate-300 dark:border-neutral-700 rounded-lg text-slate-700 dark:text-neutral-200 font-medium hover:bg-slate-50 dark:hover:bg-neutral-800 disabled:opacity-60 transition-colors"
                                            >
                                                {signInBusy
                                                    ? 'Opening Google\u2026'
                                                    : signedIn ? 'Signed in \u2713  \u00b7  Switch account' : 'Continue with Google'}
                                            </button>
                                            {signInError && (
                                                <p className="text-xs text-rose-600 dark:text-rose-400">{signInError}</p>
                                            )}
                                            <label className="block text-xs font-medium text-slate-600 dark:text-neutral-300">
                                                Google Cloud project ID
                                                <input
                                                    type="text"
                                                    value={externalConfig.projectId || ''}
                                                    onChange={(e) => setExternalConfig(prev => ({ ...prev, projectId: e.target.value }))}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                    placeholder="my-project-123456"
                                                    className="mt-1 w-full px-3 py-2 bg-white dark:bg-neutral-900 border border-slate-300 dark:border-neutral-700 rounded-lg text-slate-800 dark:text-neutral-100 placeholder-slate-400 focus:border-indigo-500 focus:outline-none"
                                                />
                                            </label>
                                            <p className="text-xs text-slate-500 dark:text-neutral-400">
                                                Required. Usage is billed to this project&apos;s Gemini quota,
                                                and its free tier applies.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className={authMethod === 'apikey' ? '' : 'hidden'}>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-neutral-200 mb-2">
                                        API Key
                                    </label>
                                    <input
                                        type="password"
                                        value={externalConfig.apiKey}
                                        onChange={(e) => setExternalConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        placeholder="Enter your Google AI API key"
                                        className="w-full px-3 py-2 bg-white dark:bg-neutral-900 border border-slate-300 dark:border-neutral-700 rounded-lg text-slate-800 dark:text-neutral-100 placeholder-slate-500 focus:border-indigo-500 focus:outline-none transition-all duration-200"
                                    />
                                    <div className="text-xs text-slate-600 dark:text-neutral-300 mt-1">
                                        Get your API key from <span className="text-indigo-600 dark:text-indigo-400 font-medium">Google AI Studio</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-neutral-800 mt-4 sticky bottom-0 bg-white dark:bg-neutral-900 pb-1">
                            <button
                                onClick={handleSave}
                                disabled={
                                    (activeTab === 'external' && authMethod === 'apikey' && !externalConfig.apiKey.trim()) ||
                                    (activeTab === 'external' && authMethod === 'code-assist' && !caReady) ||
                                    (activeTab === 'external' && authMethod === 'oauth' && (!signedIn || !externalConfig.projectId?.trim())) ||
                                    (activeTab === 'local' && (!localConfig.address.trim() || !localConfig.model.trim())) ||
                                    (activeTab === 'webllm' && !webllmConfig.model)
                                }
                                className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded font-medium hover:from-blue-500 hover:to-purple-500 disabled:from-gray-600 disabled:to-gray-600 dark:disabled:from-neutral-700 dark:disabled:to-neutral-700 disabled:cursor-not-allowed transition-all duration-200 group relative overflow-hidden"
                                onMouseEnter={(e) => {
                                    if (!e.target.disabled) {
                                        e.target.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.3)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.boxShadow = 'none';
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                                <div className="flex items-center gap-2 justify-center relative z-10">
                                    <Settings size={16} />
                                    Apply Settings
                                </div>
                            </button>

                            <button
                                onClick={() => setIsOpen(false)}
                                className="px-4 py-2 bg-gray-700 dark:bg-neutral-700 text-gray-300 dark:text-neutral-200 rounded font-medium hover:bg-gray-600 dark:hover:bg-neutral-600 hover:text-white transition-all duration-200"
                                onMouseEnter={(e) => {
                                    e.target.style.boxShadow = '0 0 10px rgba(107, 114, 128, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.boxShadow = 'none';
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModelSelector;